/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useEffect, useRef, useState } from "react";

import { useDispatch } from "react-redux";
import styled from "styled-components";

import { SearchResultsItem, StyledLink as StyledCommand } from "src/components/Search/Hits/HitComponents";
import { ResetIcon } from "src/components/Search/Hits/Icons";
import { Tooltip } from "src/components/UI/Tooltip";
import { searchActions } from "src/store/modules/search";

import {
  SearchIconWrapper,
  StyledInput,
  ResetIconWrapper,
  Divider,
  CancelButton,
  SearchBarContainer,
  SearchBar,
} from "./SearchComponents";
import { SearchIcon } from "./SearchIcon";

export const BasicSearchBox = ({
  options,
}: {
  options: { label: string; applyFunc: (...args: any) => void }[];
}) => {
  const dispatch = useDispatch();

  const inputRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState<string>("");
  const [selected, setSelected] = useState<{ label: string; applyFunc: (...args: any) => void } | null>(
    options[0]
  );

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (search.length > 0) {
      const searchResults = options.filter((option) => option.label.toLowerCase().includes(search.toLowerCase()));
      setSelected(searchResults[0]);
    }
  }, [search]);

  const handleClose = () => {
    dispatch(searchActions.searchModalClosed({}));
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.currentTarget.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.code === "Escape") {
      e.preventDefault();
      handleClose();
    }
    if (e.code === "ArrowUp") {
      e.preventDefault();
      const searchResults = options.filter((option) => option.label.toLowerCase().includes(search.toLowerCase()));
      const indexOfSelection = searchResults.indexOf(selected || options[0]);
      const newIndex = indexOfSelection > 0 ? indexOfSelection - 1 : 0;
      const newSelection = searchResults[newIndex];
      setSelected(newSelection);
    }
    if (e.code === "ArrowDown") {
      e.preventDefault();
      const searchResults = options.filter((option) => option.label.toLowerCase().includes(search.toLowerCase()));
      const indexOfSelection = searchResults.indexOf(selected || options[0]);
      const newIndex =
        indexOfSelection < searchResults.length - 1 ? indexOfSelection + 1 : searchResults.length - 1;
      const newSelection = searchResults[newIndex];
      setSelected(newSelection);
    }
    if (e.code === "Enter") {
      selected?.applyFunc(dispatch);
      dispatch(searchActions.searchModalClosed({}));
    }
  };

  return (
    <>
      <SearchBarContainer>
        <SearchBar>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <StyledInput
            placeholder="search shortcuts"
            value={search}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            ref={inputRef}
          />
          {search !== "" && (
            <ResetIconWrapper
              onClick={() => {
                setSearch("");
              }}
            >
              <ResetIcon />
            </ResetIconWrapper>
          )}
          <Divider />
          <Tooltip tooltipText="Cancel (ESC)">
            <CancelButton onClick={handleClose}>Cancel</CancelButton>
          </Tooltip>
        </SearchBar>
      </SearchBarContainer>
      <SearchResultsContainer>
        {options
          .filter((option) => option.label.toLowerCase().includes(search.toLowerCase()))
          .map((option, index) => (
            <SearchResultsItem
              onMouseEnter={() => {
                setSelected(option);
              }}
              onMouseLeave={() => {
                setSelected(null);
              }}
              key={index}
              focused={(selected && selected.label === option.label) || false}
            >
              <StyledCommand
                onClick={() => {
                  option.applyFunc(dispatch);
                  dispatch(searchActions.searchModalClosed({}));
                }}
              >
                <div key={index}>{option.label}</div>
              </StyledCommand>
            </SearchResultsItem>
          ))}
      </SearchResultsContainer>
    </>
  );
};

const SearchResultsContainer = styled.div`
  border: 0 solid rgba(174, 174, 174, 1);
  overflow-y: auto;
`;
